import { Alert, Select, Toggle } from '@cimpress/react-components';
import { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios, { type AxiosError } from 'axios';
import { ApiKeyContext } from '../App';
import { CardBuilder, Control, Page, PreviewRow, PreviewRows, PreviewSection, SurfaceSelector } from '../Components';
import { ComposeControls, ComposePresetSelector, useCompositeParams } from '../Components/CompositionCompose';
import { type Card, CustomSelector } from '../Components/common';
import { useSampleDocuments } from '../Components/common/useSampleDocuments';
import { getComposeUrl, getEndpointUrl } from '../api';
import presetSurfaces from '../data/generateSurfaceJson.json';
import presetSurfaceUrls from '../data/generateSurfaceUrls.json';
import { brandProfileUrls } from '../data/composeBrandProfileDocuments';
import { isUrl } from '../tools';
import { PRIORITY_OPTIONS } from '../constants';

function generateRefreshKey() {
    return uuidv4();
}

function isDynamicLineUrl(url: string): boolean {
    return url.includes('diamond');
}

const TITLE_COLOR = '#0088A9';

export const CompositePage = () => {
    const documents = useSampleDocuments('composite_source_documents.json');

    const {
        targetDocumentUrl,
        components,
        priority,
        surfaceSpecificationUrl,
        surfaceSpecification,
        applyOnlyToPlaceholders,
        fillDefaultText,
        isBetaEndpoint: useBeta,
        smartCrop,
        brandProfileUrl,
        enableMulticolorLogoContrastFix,
        enableMultiColorLogoToMonochromeConversion,
        setTargetDocumentUrl,
        setComponents,
        setPriority,
        setSurfaceSpecificationUrl,
        setSurfaceSpecification,
        setApplyOnlyToPlaceholders,
        setFillDefaultText,
        setSmartCrop,
        setIsBetaEndpoint: setUseBeta,
        setBrandProfileUrl,
        setEnableMulticolorLogoContrastFix,
        setEnableMultiColorLogoToMonochromeConversion,
    } = useCompositeParams();
    const [refreshKey, setRefreshKey] = useState<string>(generateRefreshKey);
    const [targetDocumentRow, setTargetDocumentRow] = useState<Card[]>([]);
    const [resultDocumentRow, setResultDocumentRow] = useState<Card[]>([]);
    const apiKey = useContext(ApiKeyContext);
    const [isBetaAvailable, setIsBetaAvailable] = useState(true);

    const setDocumentUrl = (docUrl: string) => {
        if (isUrl(docUrl)) {
            setTargetDocumentUrl(docUrl);
            return;
        }
        if (!docUrl) {
            setTargetDocumentUrl('');
        }
    };

    useEffect(() => {
        if (targetDocumentUrl) {
            let targetDocUrlForCurrentCall = targetDocumentUrl;
            if (isDynamicLineUrl(targetDocumentUrl)) {
                // Calling Diamond directly from the Playground leads to a CORS error, which is why we have to first call Compose without assets
                // in order to get a preview of the dynamic line before personalization
                targetDocUrlForCurrentCall = getComposeUrl({
                    documentUrl: targetDocumentUrl,
                    assets: {},
                    priority: 'quality',
                    fillDefaultText: true,
                    apiKey,
                    brandProfileUrl,
                    featureFlags: {
                        enableMultiColorLogoToMonochromeConversion,
                    },
                })!;
            }

            const targetCard = CardBuilder.ofType('documentUrl')
                .withTitle('Target Document')
                .withData({ documentUrl: targetDocUrlForCurrentCall })
                .build();
            setTargetDocumentRow(PreviewRow(targetCard));
        } else {
            setTargetDocumentRow(PreviewRow());
        }
    }, [targetDocumentUrl, apiKey, brandProfileUrl, enableMultiColorLogoToMonochromeConversion]);

    useEffect(() => {
        if (targetDocumentUrl && (components || brandProfileUrl)) {
            const resultCard = CardBuilder.ofType('documentUrl')
                .withTitle('Result Document')
                .withData({
                    documentUrl:
                        getComposeUrl({
                            documentUrl: targetDocumentUrl,
                            assets: components,
                            apiKey,
                            priority: priority.value,
                            surfaceSpecificationUrl,
                            surfaceSpecification,
                            version: useBeta ? 'v0' : 'v3',
                            applyOnlyToPlaceholders,
                            smartCrop,
                            fillDefaultText,
                            brandProfileUrl,
                            featureFlags: {
                                enableMulticolorLogoContrastFix,
                                enableMultiColorLogoToMonochromeConversion,
                            },
                        }) ?? '',
                    backgroundColor: 'FFFFFF',
                })
                .build();
            setResultDocumentRow(PreviewRow(resultCard));
        } else {
            setResultDocumentRow(PreviewRow());
        }
    }, [
        targetDocumentUrl,
        components,
        apiKey,
        priority,
        surfaceSpecificationUrl,
        surfaceSpecification,
        useBeta,
        applyOnlyToPlaceholders,
        smartCrop,
        fillDefaultText,
        brandProfileUrl,
        enableMulticolorLogoContrastFix,
        enableMultiColorLogoToMonochromeConversion,
    ]);

    useEffect(() => {
        const checkBetaEndpoint = async () => {
            try {
                // TODO: Add HEAD or OPTIONS methods to check endpoint availability
                // This call should always fail as it is missing key parameters for a get request
                const url = getEndpointUrl({ version: 'v0', endpoint: 'composition:compose', apiKey });
                await axios.get(url);
            } catch (error) {
                if ((error as AxiosError).response?.status === 404) {
                    setIsBetaAvailable(false);
                }
            }
        };
        checkBetaEndpoint();
    }, [apiKey]);

    return (
        <Page>
            <Page.Title>Compose</Page.Title>

            <Page.Controls>
                {isBetaAvailable && (
                    <div style={{ alignItems: 'center' }}>
                        <Toggle
                            on={isBetaAvailable && useBeta}
                            onClick={() => setUseBeta(!useBeta)}
                            onText="Beta"
                            disabled={!isBetaAvailable}
                            offText={isBetaAvailable ? 'off' : 'N/A'}
                        />
                        <span style={{ marginLeft: '10px' }}>Use Beta Endpoint</span>
                    </div>
                )}

                <Control>
                    <Control.Title color={TITLE_COLOR}>Priority</Control.Title>
                    <Select label="priority" value={priority} onChange={setPriority} options={PRIORITY_OPTIONS} />
                </Control>
                <Control>
                    <Control.Title color={TITLE_COLOR}>Template/Document</Control.Title>
                    <CustomSelector
                        selectorLabel={'Document (or template)'}
                        textfieldLabel={'Document URL or Template URL '}
                        data={documents}
                        value={targetDocumentUrl}
                        setValue={setDocumentUrl}
                        validator={isUrl}
                    />
                </Control>

                <Control>
                    <Control.Title color={TITLE_COLOR}>Assets</Control.Title>
                </Control>

                <Alert status="info" message="Select either preset assets or add your own assets" dismissible={false} />

                <ComposePresetSelector
                    onSelect={(components) => {
                        setComponents(components);
                        setRefreshKey(generateRefreshKey());
                    }}
                />

                <ComposeControls
                    // https://legacy.reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
                    key={refreshKey}
                    initialComponents={components}
                    setComponents={setComponents}
                />
                <Control>
                    <Control.Title color={TITLE_COLOR}>Brand Profile</Control.Title>
                    <CustomSelector
                        selectorLabel="Brand Profile"
                        textfieldLabel="Brand Profile URL"
                        data={brandProfileUrls}
                        value={brandProfileUrl}
                        setValue={setBrandProfileUrl}
                        validator={isUrl}
                    />
                </Control>

                <Control>
                    <Control.Title color={TITLE_COLOR}>Surface Selection</Control.Title>
                    <p>
                        A surface specification should be provided to avoid any unwanted update in a layout that
                        contains folding guides.
                    </p>
                    <SurfaceSelector
                        surfaceSpecificationUrl={surfaceSpecificationUrl}
                        setSurfaceSpecificationUrl={setSurfaceSpecificationUrl}
                        surfaceSpecification={surfaceSpecification}
                        setSurfaceSpecification={setSurfaceSpecification}
                        presetSurfaceUrlsFromPage={presetSurfaceUrls}
                        presetSurfaceJsonFromPage={presetSurfaces}
                    />
                </Control>
                <div style={{ alignItems: 'center' }}>
                    <Toggle
                        on={applyOnlyToPlaceholders}
                        onClick={() => setApplyOnlyToPlaceholders(!applyOnlyToPlaceholders)}
                        size="sm"
                    />
                    <span style={{ marginLeft: '10px' }}>Apply Only to Placeholders</span>
                </div>
                <div style={{ alignItems: 'center' }}>
                    <Toggle on={fillDefaultText} onClick={() => setFillDefaultText(!fillDefaultText)} size="sm" />
                    <span style={{ marginLeft: '10px' }}>Fill Default Texts</span>
                </div>
                {priority.value === 'speed' && (
                    <div style={{ alignItems: 'center' }}>
                        <Toggle on={smartCrop} onClick={() => setSmartCrop(!smartCrop)} size="sm" />
                        <span style={{ marginLeft: '10px' }}>Use Smart Crop</span>
                    </div>
                )}
                <div style={{ alignItems: 'center' }}>
                    <Toggle
                        on={enableMulticolorLogoContrastFix}
                        onClick={() => setEnableMulticolorLogoContrastFix(!enableMulticolorLogoContrastFix)}
                        size="sm"
                    />
                    <span style={{ marginLeft: '10px' }}>Enable Multicolor Logo Contrast Fix</span>
                </div>
                <div style={{ alignItems: 'center' }}>
                    <Toggle
                        on={enableMultiColorLogoToMonochromeConversion}
                        onClick={() =>
                            setEnableMultiColorLogoToMonochromeConversion(!enableMultiColorLogoToMonochromeConversion)
                        }
                        size="sm"
                    />
                    <span style={{ marginLeft: '10px' }}>Enable Multi Color Logo To Monochrome Conversion</span>
                </div>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={
                        'This is a page to test the compose endpoint. Fill in the target and assets to see the composed result.'
                    }
                    showIntro={!targetDocumentUrl || (!components && !brandProfileUrl)}
                    rows={PreviewRows(targetDocumentRow, resultDocumentRow)}
                />
            </Page.Content>
        </Page>
    );
};
